/*
 * Oppie receptenoverzicht (sections/oppie-recipe-index.liquid).
 *
 * Filteren en pagineren gebeurt op de server (tag-URL's en ?page=). Dit
 * script doet alleen het zoeken:
 * - zonder JS is het zoekveld een gewoon formulier naar /search
 * - met JS zoekt het live door ALLE recepten, niet alleen de huidige pagina.
 *   De data komt een keer uit /blogs/<blog>?view=zoekindex (JSON).
 * Leeg veld = de oorspronkelijke pagina terug, met paginering.
 * Lukt het ophalen niet, dan valt het terug op het gewone formulier.
 */

const MIN_QUERY = 2;
const DEBOUNCE_MS = 180;

const normalize = (value) =>
  (value || '')
    .toLowerCase()
    .normalize('NFD')
    .replace(/[̀-ͯ]/g, '')
    .trim();

const toHandle = (value) =>
  normalize(value)
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '');

// Liquid's t-filter geeft HTML-entities terug (&quot;). Die horen niet in textContent.
const decodeEntities = (value) => {
  const area = document.createElement('textarea');
  area.innerHTML = value || '';
  return area.value;
};

const parseDeviceMap = (value) => {
  const map = new Map();
  (value || '')
    .split('|')
    .filter(Boolean)
    .forEach((pair) => {
      const [handle, label] = pair.split('=');
      if (handle && label) map.set(handle, label);
    });
  return map;
};

class RecipeSearch {
  constructor(root) {
    this.root = root;
    this.form = root.querySelector('[data-recipe-search-form]');
    this.input = root.querySelector('[data-recipe-search]');
    this.clear = root.querySelector('[data-recipe-search-clear]');
    this.grid = root.querySelector('[data-recipe-grid]');
    this.count = root.querySelector('[data-recipe-count]');
    this.pagination = root.querySelector('[data-recipe-pagination]');
    this.empty = root.querySelector('[data-recipe-empty]');
    this.emptyTitle = root.querySelector('[data-recipe-empty-title]');
    this.template = root.querySelector('[data-recipe-card-template]');
    this.config = this.readConfig();
    this.devices = parseDeviceMap(this.config.devices);
    this.data = null;
    this.failed = false;
    this.timer = null;

    if (!this.form || !this.input || !this.template || !this.config.source) return;

    this.originalItems = this.grid ? [...this.grid.children] : [];
    this.originalCount = this.count ? this.count.innerHTML : '';
    this.bind();
  }

  readConfig() {
    const node = this.root.querySelector('[data-recipe-config]');
    if (!node) return {};
    try {
      const config = JSON.parse(node.textContent);
      ['noResults', 'countOne', 'countOther'].forEach((key) => {
        config[key] = decodeEntities(config[key]);
      });
      return config;
    } catch (error) {
      console.error('Oppie recepten: config onleesbaar', error);
      return {};
    }
  }

  bind() {
    this.form.addEventListener('submit', (event) => {
      if (this.failed) return;
      event.preventDefault();
      this.run();
    });

    this.input.addEventListener('input', () => {
      this.clear.hidden = this.input.value === '';
      window.clearTimeout(this.timer);
      this.timer = window.setTimeout(() => this.run(), DEBOUNCE_MS);
    });

    // Data alvast ophalen zodra iemand het veld aanraakt.
    this.input.addEventListener('focus', () => this.load(), { once: true });

    this.clear.addEventListener('click', () => this.reset());
    this.root.querySelector('[data-recipe-reset]')?.addEventListener('click', () => this.reset());
  }

  load() {
    if (!this.data) {
      this.data = fetch(this.config.source, { headers: { Accept: 'application/json' } })
        .then((response) => {
          if (!response.ok) throw new Error(`HTTP ${response.status}`);
          return response.json();
        })
        .then((json) => (json.recipes || []).map((recipe) => ({ ...recipe, haystack: normalize(recipe.search) })))
        .catch((error) => {
          console.error('Oppie recepten: zoekindex niet geladen', error);
          this.failed = true;
          return null;
        });
    }
    return this.data;
  }

  async run() {
    const query = normalize(this.input.value);
    if (query.length < MIN_QUERY) {
      this.restore();
      return;
    }

    const recipes = await this.load();
    if (!recipes) {
      this.form.submit();
      return;
    }

    const words = query.split(/\s+/).filter(Boolean);
    const matches = recipes.filter((recipe) => words.every((word) => recipe.haystack.includes(word)));
    this.render(matches, this.input.value.trim());
  }

  render(matches, rawQuery) {
    if (this.pagination) this.pagination.hidden = true;
    const cards = matches.map((recipe) => this.buildCard(recipe));
    if (this.grid) {
      this.grid.replaceChildren(...cards);
      this.grid.hidden = cards.length === 0;
    }

    if (this.count) {
      const template = matches.length === 1 ? this.config.countOne : this.config.countOther;
      this.count.textContent = (template || '').replace('[n]', String(matches.length)).replace('[q]', rawQuery);
      this.count.hidden = matches.length === 0;
    }

    if (this.empty) {
      this.empty.hidden = matches.length > 0;
      if (this.emptyTitle) this.emptyTitle.textContent = (this.config.noResults || '').replace('[q]', rawQuery);
    }
  }

  restore() {
    if (this.grid) {
      this.grid.replaceChildren(...this.originalItems);
      this.grid.hidden = false;
    }
    if (this.count) {
      this.count.innerHTML = this.originalCount;
      this.count.hidden = false;
    }
    if (this.pagination) this.pagination.hidden = false;
    if (this.empty) this.empty.hidden = true;
  }

  reset() {
    this.input.value = '';
    this.clear.hidden = true;
    this.restore();
    this.input.focus();
  }

  deviceLabel(recipe) {
    const tag = (recipe.tags || []).map(toHandle).find((handle) => this.devices.has(handle));
    return tag ? this.devices.get(tag) : recipe.product || '';
  }

  buildCard(recipe) {
    const fragment = this.template.content.cloneNode(true);
    const item = fragment.firstElementChild;
    const field = (name) => item.querySelector(`[data-field="${name}"]`);

    item.querySelector('a').href = recipe.url;
    field('title').textContent = recipe.title;

    const image = field('image');
    if (recipe.image) {
      image.src = recipe.image;
      image.alt = recipe.alt || recipe.title;
      field('placeholder').remove();
    } else {
      image.remove();
    }

    if (recipe.group) field('group').textContent = recipe.group;
    else field('group').remove();

    const device = this.deviceLabel(recipe);
    if (recipe.time) field('time').textContent = recipe.time;
    else field('time-row').remove();
    if (device) field('device').textContent = device;
    else field('device-row').remove();
    if (!recipe.time && !device) field('meta').remove();

    return item;
  }
}

document.querySelectorAll('[data-recipe-index]').forEach((root) => new RecipeSearch(root));
